import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { faEdit, faTrash, faCopy } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import './InstancesList.scss';
import { Button, Card, Modal, Stack, Tooltip, Typography } from '@mui/material';
import { BaseSyntheticEvent, useCallback, useEffect, useState } from 'react';
import i18next from 'i18next';
import { DataGrid, enUS, frFR, GridCellParams, GridColDef, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarExport, GridToolbarFilterButton } from '@mui/x-data-grid';
import axios from 'axios';
import _ from 'lodash';
import { Box } from '@mui/system';
import { useDispatch, useSelector } from 'react-redux';
import { AuthHeader } from '../../../utils/helpers/auth-headers';
import { setAlertData } from '../../../stores/alertStore';
import { AlertSeverity } from '../../../utils/enum/Alerts';
import { PexipInstance } from '../../../models/PexipInstance';
import { PageRowsOptions } from '../../../models/DataGridConf';
import { RootState } from '../../../stores/globalStore';
import { setConnectedUser } from '../../../stores/authenticationStore';

function InstancesList() {
  const { t } = useTranslation();

  const i18nLang = i18next.language;
  const [gridLang, setGridLang] = useState(frFR);

  const connectedUser = useSelector((state: RootState) => state.authentication.connectedUser);

  const dispatch = useDispatch();

  const path = useLocation().pathname;
  const navigate = useNavigate();
  const [instances, setInstances] = useState<PexipInstance[]>();
  const [openModal, setOpenModal] = useState(false);
  const [deletedInstance, setDeletedInstance] = useState<PexipInstance>();
  const [pageSize, setPageSize] = useState<number>(connectedUser.prefs?.nbRow ? connectedUser.prefs.nbRow : 10);
  const pageRowsOptions = PageRowsOptions;

  const { instanceId } = useParams();

  const [selectionModel, setSelectionModel] = useState<any[]>([]);

  const getInstances = () => {
    axios.get(`${window._env_.REACT_APP_API_URI}/pexipInstance?sort=-index`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => {
        setInstances(response.data.data);
      })
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
          dispatch(setAlertData({
            message: err.data.message,
            severity: AlertSeverity.ERROR,
            open: true,
          }));
        }
        console.error(error);
      });
  };

  useEffect(() => {
    getInstances();
  }, [navigate]);

  useEffect(() => {
    if (!path.includes('/instances/')) {
      setSelectionModel([]);
    } else {
      setSelectionModel([instanceId]);
    }
  }, [navigate]);

  useEffect(() => {
    if (i18nLang === 'en') {
      setGridLang(enUS);
    } else {
      setGridLang(frFR);
    }
  }, [i18nLang]);

  const editInstance = (ev: any, param: PexipInstance) => {
    navigate(`/configuration/instances/edit/${param._id}`);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const openDeleteModal = (ev: BaseSyntheticEvent, param: PexipInstance) => {
    setDeletedInstance(param);
    setOpenModal(true);
  };

  const deleteInstance = (instance: PexipInstance) => {
    axios.delete(`${window._env_.REACT_APP_API_URI}/pexipInstance/${instance._id}`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then(() => {
        let fullInstances = instances;
        fullInstances = _.reject(fullInstances, { _id: instance._id });
        setInstances(fullInstances);
        handleCloseModal();
      })
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
          dispatch(setAlertData({
            message: err.data.message,
            severity: AlertSeverity.ERROR,
            open: true,
          }));
        }
        console.error(error);
      });
  };

  const changeIndex = (instance: PexipInstance) => {
    axios.patch(`${window._env_.REACT_APP_API_URI}/pexipInstance/${instance._id}`, instance, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then(() => {
        getInstances();
      })
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
          dispatch(setAlertData({
            message: err.data.message,
            severity: AlertSeverity.ERROR,
            open: true,
          }));
        }
        console.error(error);
      });
  };

  const columns: GridColDef[] = [
    {
      field: '_id', headerName: 'ID', minWidth: 90, flex: 1, hide: true
    },
    {
      field: 'name',
      renderHeader: () => (
        <span className="strong-header">{ t('InstancesList.table.name') }</span>
      ),
      minWidth: 150,
      flex: 1
    },
    {
      field: 'isVirtuvisio',
      renderHeader: () => (
        <span className="strong-header">{ t('InstancesList.table.isVirtuvisio') }</span>
      ),
      minWidth: 110,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        if (params.row.isVirtuvisio) {
          return (<span>{ t('InstancesList.table.yes') }</span>);
        }
        return (<span>{ t('InstancesList.table.no') }</span>);
      }
    },
    {
      field: 'pexipServerUrl',
      renderHeader: () => (
        <span className="strong-header">{ t('InstancesList.table.pexipServerUrl') }</span>
      ),
      minWidth: 150,
      flex: 1
    },
    {
      field: 'pexipServerUser',
      renderHeader: () => (
        <span className="strong-header">{ t('InstancesList.table.pexipServerUser') }</span>
      ),
      minWidth: 150,
      flex: 1
    },
    {
      field: 'virtuvisioServerUrl',
      renderHeader: () => (
        <span className="strong-header">{ t('InstancesList.table.virtuvisioServerUrl') }</span>
      ),
      minWidth: 150,
      flex: 1
    },
    {
      field: 'virtuvisioServerUser',
      renderHeader: () => (
        <span className="strong-header">{ t('InstancesList.table.virtuvisioServerUser') }</span>
      ),
      minWidth: 150,
      flex: 1
    },
    {
      field: 'index',
      renderHeader: () => (
        <span className="strong-header">{ t('InstancesList.table.priority') }</span>
      ),
      maxWidth: 150,
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <input
          type="number"
          id="number"
          min="0"
          pattern="[0-9]{0,5}"
          style={{ maxWidth: '100px' }}
          value={params.row.index}
          onChange={(event) => {
            if (!event.target.value) {
              event.target.value = '0';
            }
            params.row.index = parseInt(event.target.value, 10);
            changeIndex(params.row);
          }}
        />
      )
    },
    {
      field: 'actions',
      renderHeader: () => (
        <span className="strong-header">{ t('InstancesList.table.actions') }</span>
      ),
      minWidth: 230,
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <>
          <Tooltip title={t('InstancesList.button.edit') as string} arrow>
            <Button
              variant="contained"
              color="primary"
              sx={{ marginRight: 1 }}
              onClick={(event: any) => {
                editInstance(event, params.row);
              }}
            >
              <FontAwesomeIcon icon={faEdit} />
            </Button>
          </Tooltip>
          <Tooltip title={t('InstancesList.button.delete') as string} arrow>
            <Button
              variant="contained"
              color="secondary"
              sx={{ marginRight: 1 }}
              onClick={(event: any) => {
                openDeleteModal(event, params.row);
              }}
              disabled={path.includes('users/edit/')}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </Tooltip>
          <Tooltip title={t('InstancesList.button.copyEventSinkLink') as string} arrow>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                navigator.clipboard.writeText(`${window._env_.REACT_APP_EVENT_SINK_URI}/eventSink/${params.row._id}`);
              }}
            >
              <FontAwesomeIcon icon={faCopy} />
            </Button>
          </Tooltip>
        </>
      )
    }
  ];

  const handleCreateButton = () => {
    navigate('/configuration/instances/create');
  };

  const CreateButton = useCallback(() => (
    <Button variant="contained" onClick={handleCreateButton}>{t('InstancesList.button.create')}</Button>
  ), []);

  const CustomToolbar = useCallback(() => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
      <CreateButton />
    </GridToolbarContainer>
  ), []);

  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    borderRadius: '12px',
    boxShadow: 24,
    p: 4,
  };

  const deleteModal = () => (
    deletedInstance && (
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            { t('InstancesList.modal.title') }
          </Typography>
          <Typography id="modal-modal-description" sx={{ my: 2 }}>
            { t('InstancesList.modal.content') }
            {' '}
            <b>{deletedInstance.name}</b>
            {' '}
            ?
          </Typography>
          <Stack direction="row" spacing={2} alignSelf="center" width="100%">
            <Button fullWidth type="reset" onClick={handleCloseModal} color="secondary" variant="contained">{ t('InstancesList.button.cancel') }</Button>
            <Button fullWidth type="submit" onClick={() => deleteInstance(deletedInstance)} variant="contained">{ t('InstancesList.button.submit') }</Button>
          </Stack>
        </Box>
      </Modal>
    )
  );

  const updateNbRowUser = (updateNbRow: number) => {  
    const updatePrefs = { ...connectedUser.prefs };
    updatePrefs.nbRow = updateNbRow;
    const modifyPrefs = {
      prefs: updatePrefs
    };
    axios.post(`${window._env_.REACT_APP_API_URI}/user/prefs`, modifyPrefs, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => {
        dispatch(setConnectedUser({ connectedUser: response.data.data }));
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const changePageSize = (newPage: number) => {
    setPageSize(newPage);
    updateNbRowUser(newPage);
  };

  return (
    <>
      <div className={(path.includes('instances/edit/') || path.includes('instances/create')) ? 'instances-bloc outlet-open' : 'instances-bloc'}>
        <div className="users-list">
          <div className="category-container">
            <Card>
              <div>
                {instances && (
                  <DataGrid
                    getRowId={(r) => r._id}
                    rows={instances}
                    columns={columns}
                    rowsPerPageOptions={pageRowsOptions}
                    pageSize={pageSize}
                    onPageSizeChange={(newPage) => changePageSize(newPage)}
                    components={{ Toolbar: CustomToolbar }}
                    pagination
                    autoHeight
                    hideFooterSelectedRowCount
                    onSelectionModelChange={setSelectionModel}
                    selectionModel={selectionModel}
                    localeText={gridLang.components.MuiDataGrid.defaultProps.localeText}
                  />
                )}
              </div>
            </Card>
          </div>
        </div>
        <div className="users-outlet">
          <Outlet />
        </div>
      </div>
      {deleteModal()}
    </>
  );
}

export default InstancesList;
